// PEOPLE: search and filter the whole network, open anyone in a drawer, keep private notes.
import { INVESTOR_TYPES, REGIONS, SECTORS, SENSITIVE_WINDOW_DAYS, type Contact, type Network, type PrivateNote } from "../../shared/network";
import { CADENCE, daysBetween, epochDay, fmtMoney, holdLabel } from "../../shared/engine";
import { toCsv } from "../lib/csv";
import { buildXlsx } from "../lib/xlsx-write";
import type { AppContext, MountFn } from "../context";
import { h, mount, debounce, fmtDay, num } from "../ui/dom";
import { iata } from "../ui/iata";
import { emptyState, key, seg, sw, ticks } from "../ui/widgets";

interface Filters {
  q: string;
  type: string;
  sector: string;
  region: string;
  minStr: number;
  flagsOnly: boolean;
  sort: string;
  dir: 1 | -1;
}
const F: Filters = { q: "", type: "", sector: "", region: "", minStr: 0, flagsOnly: false, sort: "name", dir: 1 };

const FLAG_CODE: Record<string, string> = { "deceased": "DEC", "loss of child": "LOSS", "serious illness": "ILL", "divorce": "DIV", "inactive": "INA", "retired": "RET", "sold company": "SOLD", "new role": "NEW", "family milestone": "FAM" };

interface Idx {
  hay: string[];
  last: (number | null)[];
}
const indexes = new WeakMap<Network, Idx>();
function indexOf(net: Network): Idx {
  let x = indexes.get(net);
  if (!x) {
    x = {
      hay: net.contacts.map((c) => [c.n, c.co, c.ti, c.no, c.em, c.ci, c.st].join("\n").toLowerCase()),
      last: net.contacts.map((c) => {
        const a = c.ib.length ? c.ib[c.ib.length - 1] : -1e9;
        const b = c.ob.length ? c.ob[c.ob.length - 1] : -1e9;
        const m = Math.max(a, b);
        return m > -1e8 ? m : null;
      }),
    };
    indexes.set(net, x);
  }
  return x;
}

export const mountPeople: MountFn = (el, ctx, params) => {
  if (!ctx.network) {
    mount(el, h("div", { class: "pagehead" }, h("h1", { class: "title" }, "People")), emptyState(() => ctx.navigate("sources")));
    return;
  }
  const net = ctx.network;
  const idx = indexOf(net);
  const today = epochDay(net, ctx.asOf);
  const daysOf = (i: number): number | null => {
    const l = idx.last[i];
    const m = ctx.state.contacted[net.contacts[i].k];
    const md = m ? epochDay(net, m) : null;
    const best = md != null && (l == null || md > l) ? md : l;
    return best == null ? null : today - best;
  };

  let filtered: number[] = [];
  let limit = 100;
  let openK: string | null = params?.k ?? null;

  /* ---------- controls ---------- */
  const search = h("input", { type: "search", id: "people-search", placeholder: "Name, company, notes, email", autocomplete: "off", spellcheck: false, value: F.q, "aria-label": "Search people" });
  const count = h("span", { class: "meta mono muted" });
  const sel = (label: string, opts: string[], value: string, on: (v: string) => void) => {
    const s = h("select", { "aria-label": label }, h("option", { value: "" }, label), opts.map((o) => h("option", { value: o, selected: o === value }, o)));
    s.addEventListener("change", () => on(s.value));
    return s;
  };
  const filtersEl = h("div", { class: "filters" });
  function paintFilters() {
    mount(
      filtersEl,
      h("div", { class: "search" }, search, h("kbd", null, "/")),
      sel("ALL TYPES", INVESTOR_TYPES, F.type, (v) => ((F.type = v), apply())),
      sel("ALL SECTORS", SECTORS, F.sector, (v) => ((F.sector = v), apply())),
      sel("ALL REGIONS", REGIONS, F.region, (v) => ((F.region = v), apply())),
      seg([{ value: "0", label: "ANY" }, { value: "3", label: "3+" }, { value: "4", label: "4+" }, { value: "5", label: "5" }], String(F.minStr), (v) => ((F.minStr = +v), paintFilters(), apply())),
      key("FLAGS ONLY", () => ((F.flagsOnly = !F.flagsOnly), paintFilters(), apply()), { on: F.flagsOnly }),
      key("CLEAR", () => (Object.assign(F, { q: "", type: "", sector: "", region: "", minStr: 0, flagsOnly: false }), (search.value = ""), paintFilters(), apply()), { sm: true }),
      h("span", { class: "grow", style: "flex:1" }),
      key("EXPORT CSV", () => exportView("csv"), { sm: true, title: "Export this view. Private notes and holds are never included." }),
      key("EXPORT XLSX", () => exportView("xlsx"), { sm: true, title: "Export this view. Private notes and holds are never included." }),
    );
  }
  search.addEventListener("input", debounce(() => ((F.q = search.value), apply()), 120));

  const tbody = h("tbody");
  const cols: { label: string; sort?: string; cls?: string }[] = [
    { label: "NAME", sort: "name" }, { label: "COMPANY", sort: "company" }, { label: "TYPE", cls: "hm" }, { label: "SECTORS", cls: "hm" },
    { label: "CHECK", sort: "check", cls: "hm num" }, { label: "LOC" }, { label: "STR", sort: "str" }, { label: "DAYS", sort: "days", cls: "num" }, { label: "FLAGS" },
  ];
  const thead = h("thead");
  function paintHead() {
    mount(
      thead,
      h(
        "tr",
        null,
        cols.map((c) => {
          const th = h("th", { class: [c.cls, c.sort ? "sort" : "", F.sort === c.sort ? "sorted" : ""].filter(Boolean).join(" ") }, c.label + (F.sort === c.sort ? (F.dir === 1 ? " ▲" : " ▼") : ""));
          if (c.sort)
            th.addEventListener("click", () => {
              if (F.sort === c.sort) F.dir = (F.dir * -1) as 1 | -1;
              else Object.assign(F, { sort: c.sort, dir: c.sort === "name" || c.sort === "company" ? 1 : -1 });
              paintHead();
              apply();
            });
          return th;
        }),
      ),
    );
  }
  const more = h("div", { class: "stripmore" });
  const wrap = h("div", { class: "table-wrap" }, h("table", null, thead, tbody));
  const drawerHost = h("div");
  mount(
    el,
    h("div", { class: "pagehead" }, h("h1", { class: "title" }, "People"), count),
    filtersEl,
    wrap,
    more,
    drawerHost,
  );
  paintFilters();
  paintHead();

  /* ---------- filtering and rows ---------- */
  function apply() {
    const toks = F.q.toLowerCase().split(/\s+/).filter(Boolean);
    const out: number[] = [];
    const cs = net.contacts;
    for (let i = 0; i < cs.length; i++) {
      const c = cs[i];
      if (F.type && c.ty !== F.type) continue;
      if (F.sector && !c.se.includes(F.sector)) continue;
      if (F.region && c.rg !== F.region) continue;
      if (F.minStr && c.rs < F.minStr) continue;
      const pn = ctx.state.notes[c.k];
      if (F.flagsOnly && !c.fl.length && !pn?.hold && !pn?.noPitch) continue;
      if (toks.length) {
        const hay = idx.hay[i] + (pn ? "\n" + pn.note.toLowerCase() : "");
        if (!toks.every((t) => hay.includes(t))) continue;
      }
      out.push(i);
    }
    const dir = F.dir;
    const val: Record<string, (i: number) => number | string> = {
      name: (i) => cs[i].n.split(" ").slice(-1)[0].toLowerCase() + cs[i].n.toLowerCase(),
      company: (i) => cs[i].co.toLowerCase(),
      check: (i) => cs[i].cmax,
      str: (i) => cs[i].rs,
      days: (i) => daysOf(i) ?? 99999,
    };
    const v = val[F.sort] ?? val.name;
    out.sort((a, b) => {
      const x = v(a), y = v(b);
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
    filtered = out;
    limit = 100;
    paintRows();
  }

  function flagCell(c: Contact): HTMLElement {
    const pn = ctx.state.notes[c.k];
    const box = h("div", { class: "chips" });
    if (pn?.hold) box.appendChild(h("span", { class: "chip hold" }, "HOLD"));
    if (pn?.noPitch) box.appendChild(h("span", { class: "chip hold" }, "NO PITCH"));
    if (pn?.note) box.appendChild(h("span", { class: "chip mine" }, "NOTE"));
    for (const f of c.fl.slice(0, 3)) box.appendChild(h("span", { class: "chip" + (f.flag in SENSITIVE_WINDOW_DAYS ? " hold" : ""), title: f.flag }, FLAG_CODE[f.flag] ?? f.flag.slice(0, 4).toUpperCase()));
    return box;
  }

  function rowEl(i: number): HTMLElement {
    const c = net.contacts[i];
    const d = daysOf(i);
    const loc = iata(c.ci, c.st);
    const cad = CADENCE[c.rs] ?? 0;
    const over = d != null && cad && cad < 100000 && d > cad;
    const tr = h(
      "tr",
      { class: "prow" + (openK === c.k ? " sel" : ""), "data-k": c.k, tabIndex: 0 },
      h("td", { class: "clip" }, h("div", { class: "nm" }, c.n), h("div", { class: "sub" }, c.ti || " ")),
      h("td", { class: "clip" }, c.co),
      h("td", { class: "hm" }, c.ty === "Unknown" ? h("span", { class: "muted" }, "Unknown") : c.ty),
      h("td", { class: "clip hm", title: c.se.join(", ") }, c.se.length ? c.se.slice(0, 2).join(", ") + (c.se.length > 2 ? ` +${c.se.length - 2}` : "") : h("span", { class: "muted" }, "—")),
      h("td", { class: "num hm" }, c.cmax ? `${fmtMoney(c.cmin)}-${fmtMoney(c.cmax)}` : h("span", { class: "muted" }, "—")),
      h("td", null, h("span", { class: "loc2" }, loc.code)),
      h("td", null, ticks(c.rs)),
      h("td", { class: "num" + (over ? " am" : "") }, d == null ? "—" : d + "D"),
      h("td", null, flagCell(c)),
    );
    const open = () => ctx.navigate("people", { k: c.k });
    tr.addEventListener("click", open);
    tr.addEventListener("keydown", (e) => (e.key === "Enter" ? open() : null));
    return tr;
  }

  function paintRows() {
    const n = Math.min(limit, filtered.length);
    mount(tbody, filtered.slice(0, n).map(rowEl));
    paintMore();
  }
  function paintMore() {
    const n = Math.min(limit, filtered.length);
    mount(count, h("b", { class: "num", style: "color:var(--text);font-weight:400" }, num(filtered.length)), ` OF ${num(net.contacts.length)}`);
    mount(more, filtered.length > n ? key(`SHOW ${Math.min(100, filtered.length - n)} MORE`, grow) : filtered.length ? null : h("div", { class: "muted mono", style: "font-size:12px" }, "NO ONE MATCHES"));
  }
  function grow() {
    const from = Math.min(limit, filtered.length);
    limit += 100;
    const to = Math.min(limit, filtered.length);
    for (let i = from; i < to; i++) tbody.appendChild(rowEl(filtered[i]));
    paintMore();
  }
  const onScroll = () => {
    if (filtered.length > limit && window.innerHeight + window.scrollY > document.documentElement.scrollHeight - 700) grow();
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  /* ---------- export (never includes private notes or holds) ---------- */
  function exportView(kind: "csv" | "xlsx") {
    const head = ["Name", "Title", "Company", "Investor type", "Sectors", "Check min", "Check max", "City", "State", "Email", "Phone", "LinkedIn", "Strength", "Days since contact"];
    const rows: (string | number)[][] = [head];
    for (const i of filtered) {
      const c = net.contacts[i];
      rows.push([c.n, c.ti, c.co, c.ty, c.se.join("; "), c.cmin, c.cmax, c.ci, c.st, c.em, c.ph, c.li, c.rs, daysOf(i) ?? ""]);
    }
    const stamp = ctx.asOf;
    if (kind === "csv") ctx.save(`radar-people-${stamp}.csv`, new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" }));
    else ctx.save(`radar-people-${stamp}.xlsx`, new Blob([buildXlsx([{ name: "People", rows }]) as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    ctx.toast(`Exported ${num(filtered.length)} people.`);
  }

  /* ---------- drawer ---------- */
  function closeDrawer() {
    ctx.navigate("people");
  }
  function openDrawer(k: string | null) {
    openK = k;
    for (const tr of tbody.querySelectorAll("tr.sel")) tr.classList.remove("sel");
    if (k) tbody.querySelector(`tr[data-k="${CSS.escape(k)}"]`)?.classList.add("sel");
    const c = k ? net.contacts.find((x) => x.k === k) : null;
    mount(drawerHost, c ? drawer(c, ctx, closeDrawer) : null);
    if (k && !c) ctx.toast("That person isn't in the network.", "warn");
  }

  const onEsc = () => {
    if (openK) closeDrawer();
  };
  document.addEventListener("radar:escape", onEsc);
  const onParams = (e: Event) => openDrawer((e as CustomEvent<Record<string, string>>).detail.k ?? null);
  el.addEventListener("params", onParams);
  const off = ctx.onChange(() => {
    // Rebuild only the rows. The drawer is left alone so a click on one of its keys isn't lost mid-save.
    const n = Math.min(limit, filtered.length);
    mount(tbody, filtered.slice(0, n).map(rowEl));
  });

  apply();
  if (openK) openDrawer(openK);
  return () => {
    off();
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("radar:escape", onEsc);
    el.removeEventListener("params", onParams);
  };
};

/* ---------- the drawer ---------- */
function drawer(c: Contact, ctx: AppContext, close: () => void): HTMLElement {
  const loc = iata(c.ci, c.st);
  const src = (s: string) => h("span", { class: "from" }, s);
  const grid = h("div", { class: "fgrid" });
  const row = (k: string, v: Node | string | null, from?: string) => {
    if (v == null || v === "") return;
    grid.appendChild(h("div", { class: "k" }, k));
    grid.appendChild(h("div", { class: "v" }, v, from ? src(from) : null));
  };
  row("TYPE", c.ty, c.tys);
  row("SECTORS", c.se.join(", "), c.ses);
  row("CHECK", c.cmax ? `${fmtMoney(c.cmin)} - ${fmtMoney(c.cmax)}` : "", c.cs);
  row("DEAL TYPES", c.dt.join(", "));
  row("STRENGTH", h("span", { class: "row", style: "gap:8px" }, ticks(c.rs), h("span", { class: "num" }, `${c.rs}/5`)));
  row("EMAIL", c.em);
  row("PHONE", c.ph);
  row("LINKEDIN", c.li ? h("a", { href: c.li, target: "_blank", rel: "noopener noreferrer", class: "cy" }, c.li.replace(/^https?:\/\/(www\.)?/, "")) : "");
  row("COMPANY", c.co);
  row("LI COMPANY", c.lco ? h("span", { class: "am" }, c.lco + "  (possible job change)") : "");
  row("LOCATION", [c.ci, c.st].filter(Boolean).join(", ") + (c.rg ? `  ·  ${c.rg}` : ""));
  row("HOW MET", c.hm);
  row("BURNED IN", c.bu.join(", "));
  row("JUST INVESTED", c.ji ? `${c.ji.sector}${c.ji.date ? " (" + c.ji.date.slice(0, 7) + ")" : ""}` : "");
  row("SAID NO TO", (c.np ?? []).join(", "));
  row("ASKED FOR", (c.as ?? []).map((a) => `${a.sector}${a.date ? " " + a.date.slice(0, 7) : ""}`).join(", "));
  row("CRM NOTES", c.no);

  const sec = (title: string, body: Node | Node[] | null) => (body == null ? null : h("div", { class: "sec" }, h("span", { class: "micro" }, title), body));

  const holdInfo = ctx.holds(c.k);
  const flags = c.fl.length
    ? h(
        "div",
        null,
        c.fl.map((f) => {
          const sens = f.flag in SENSITIVE_WINDOW_DAYS;
          return h("div", { style: "margin-bottom:6px" }, h("span", { class: "chip " + (sens ? "hold" : "") }, holdLabel(f.flag)), h("span", { class: "num muted", style: "margin-left:8px;font-size:12px" }, f.date ?? ""), h("span", { class: "muted", style: "margin-left:8px;font-size:12px" }, `${f.source}: ${f.text}`));
        }),
      )
    : null;
  const touches = c.rt.length
    ? h(
        "div",
        { class: "tlog" },
        [...c.rt].reverse().map((t) => [
          h("span", { class: "num" }, fmtDay(t[0], true).replace(/ \d{4}$/, " " + t[0].slice(2, 4))),
          h("span", { class: "muted" }, t[1].toUpperCase()),
          h("span", { class: t[2] === "I" ? "gr" : "dim", title: t[2] === "I" ? "They reached out" : "You reached out" }, t[2] === "I" ? "←" : "→"),
          h("span", { class: "sm" }, t[3]),
        ]),
      )
    : null;
  const pa = c.pa.length
    ? h(
        "div",
        { class: "tlog", style: "grid-template-columns:90px 1fr 80px 70px" },
        c.pa.map((p) => [
          h("span", { class: "num" }, p.deal),
          h("span", { class: p.outcome === "Invested" ? "gr" : p.outcome === "Passed" ? "dim" : "muted" }, p.outcome),
          h("span", { class: "num" }, p.amount ? fmtMoney(p.amount) : "—"),
          h("span", { class: "num muted" }, p.date.slice(0, 7)),
        ]),
      )
    : null;

  /* Your controls: private note, holds, contacted */
  const cur = (): PrivateNote => ctx.state.notes[c.k] ?? { note: "", hold: false, noPitch: false };
  const empty = (n: PrivateNote) => !n.note && !n.hold && !n.noPitch && !n.sectors?.length;
  const put = (n: PrivateNote) => void ctx.setNote(c.k, empty(n) ? null : n);
  const ta = h("textarea", { class: "mine", rows: 5, placeholder: "What you know that the CRM doesn't.", "aria-label": "Private note" });
  ta.value = cur().note;
  const saveNote = () => {
    if (ta.value.trim() === cur().note.trim()) return;
    put({ ...cur(), note: ta.value.trim() });
  };
  ta.addEventListener("blur", saveNote);
  const contactedToday = ctx.state.contacted[c.k] === ctx.asOf;
  const ck = key("CONTACTED TODAY", () => {
    const on = ctx.state.contacted[c.k] === ctx.asOf;
    ck.classList.toggle("on", !on);
    ck.setAttribute("aria-pressed", String(!on));
    void ctx.markContacted(c.k, on ? null : ctx.asOf);
  }, { on: contactedToday });
  const mine = h(
    "div",
    { class: "mine-box" },
    h("span", { class: "micro" }, "PRIVATE. ONLY YOU SEE THIS"),
    ta,
    h(
      "div",
      { class: "row", style: "margin-top:10px;gap:18px" },
      sw("HOLD", cur().hold, (v) => put({ ...cur(), hold: v }), true),
      sw("DON'T PITCH", cur().noPitch, (v) => put({ ...cur(), noPitch: v }), true),
      h("span", { class: "grow" }),
      key("SAVE", () => (saveNote(), ctx.toast("Saved.")), { sm: true }),
      ck,
    ),
    holdInfo.auto ? h("p", { class: "rd mono", style: "font-size:12px;margin:10px 0 0" }, "AUTO HOLD: " + holdInfo.auto.why) : null,
  );

  const sources = c.src.length ? h("div", { class: "chips" }, c.src.map((s) => h("span", { class: "lamp lit green" }, s.toUpperCase()))) : null;
  const confs = c.cf.length ? h("div", null, c.cf.map((e) => h("div", { class: "row", style: "gap:10px;margin-bottom:3px" }, h("span", null, e.name), h("span", { class: "num muted" }, e.date)))) : null;
  const gap = c.rt.length ? daysBetween(c.rt[c.rt.length - 1][0], ctx.asOf) : null;

  return h(
    "div",
    null,
    h("div", { class: "scrim", onClick: close }),
    h(
      "aside",
      { class: "drawer", role: "dialog", "aria-label": c.n },
      h(
        "div",
        { class: "dh" },
        h("div", { class: "who" }, h("div", { class: "nm" }, c.n), h("div", { class: "muted" }, [c.ti, c.co].filter(Boolean).join(" · ")), h("div", { class: "row", style: "gap:10px;margin-top:6px" }, h("span", { class: "loc2" }, loc.code), h("span", { class: "muted", style: "font-size:12px" }, loc.label), gap != null ? h("span", { class: "micro" }, `LAST TOUCH ${gap}D AGO`) : null)),
        key("CLOSE  ESC", close, { sm: true }),
      ),
      sec("YOURS", mine),
      sec("FIELDS", grid),
      sec("SOURCES", sources),
      sec("FLAGS", flags),
      sec("CONFERENCES", confs),
      sec("PAST DEALS", pa),
      sec("LAST TOUCHES", touches),
    ),
  );
}
